import { useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid2';
import startCase from 'lodash/startCase';
import { useState } from 'react';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import {
  BaseDrawer,
  BaseDrawerTitle,
  MemoizedBaseAvatar,
  CustomSingleSelect,
} from '@mgm/common/components/base';
import {
  TCreateEditForm,
} from '../../../components/form';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getCommissionPlans, getIsOpenAssignMembersNav } from '../../../store/commissions/selectors';
import { commissionsActions } from '../../../store/commissions/slice';
import { getMembers } from '../../../store/team/selectors';
import { useAssignBulkCommissionPlanAPI } from '../../../hooks/api/useCommissions';
import { TCommissionPlan } from '../../../types';
import { commonActions } from '../../../store/common/slice';

const AssignMembersNav = () => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const isOpenAssignMembersNav = useAppSelector(getIsOpenAssignMembersNav);
  const dispatch = useAppDispatch();
  const commissionPlans = useAppSelector(getCommissionPlans);
  const { mutateAsync: bulkAssign, isLoading } = useAssignBulkCommissionPlanAPI();
  const { t } = useTranslation('commissions');

  const [selectedPlans, setSelectedPlans] = useState({} as { [key: string]: TCommissionPlan });

  const methods = useForm<Exclude<TCreateEditForm,
  'password' | 'fullName' | 'position' | 'department'>>({
    mode: 'all',
  });

  const members = useAppSelector(getMembers);

  const membersWithoutPlan = members.filter((member) => !member.commissionPlan?.name);

  const handleCloseNav = () => {
    dispatch(commissionsActions.setIsOpenAssignMembersNav(false));
  };

  const assignMembers = async () => {
    const assignData = Object.keys(selectedPlans)
      .reduce((result: Array<{ commissionPlan: TCommissionPlan, memberEmails: Array<string> }>, email) => {
        const plan = selectedPlans[email];
        const existingPlanIndex = result.findIndex((item) => item.commissionPlan === plan);

        if (existingPlanIndex !== -1) {
          result[existingPlanIndex].memberEmails.push(email);
        } else {
          result.push({
            commissionPlan: plan,
            memberEmails: [email],
          });
        }

        return result;
      }, []);

    await bulkAssign(assignData);

    handleCloseNav();
  };

  const handleFormChange = (key: string, val: TCommissionPlan) => {
    setSelectedPlans((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? '100%' : 600,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isOpenAssignMembersNav}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={t('assignPlans')} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={methods.handleSubmit(assignMembers)}
        >
          <List sx={{ width: '100%', py: 3 }}>
            {membersWithoutPlan.map((member) => {
              const labelId = `checkbox-list-secondary-label-${member.email}`;

              return (
                <Stack pb={2} spacing={2} key={member.email}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size={{
                      xs: 6,
                      lg: 6,
                    }}
                    >
                      <Stack alignItems="center" direction="row" spacing={1.5}>
                        <ListItemAvatar>
                          <MemoizedBaseAvatar avatarName={member.name} avatarFile={member.avatarFile} />
                        </ListItemAvatar>
                        <ListItemText id={labelId} primary={startCase(member.name)} />
                      </Stack>
                    </Grid>
                    <Grid size={{
                      xs: 6,
                      lg: 6,
                    }}
                    >
                      <CustomSingleSelect
                        options={commissionPlans}
                        onChange={(data) => handleFormChange(member.email, data as TCommissionPlan)}
                        name={`commissionPlan-${member.email}`}
                        value={selectedPlans[member.email] || ''}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              );
            })}
          </List>
          <Box pb={2}>
            <LoadingButton
              disabled={Object.keys(selectedPlans).length === 0 || isLoading}
              loading={isLoading}
              variant="contained"
              onClick={assignMembers}
            >
              {t('assignPlans')}
            </LoadingButton>
          </Box>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  AssignMembersNav,
};
